import type { ThemeOverrideChannels } from '@bridebook/web/lib/wedding-website/motif/resolve-css';
import {
  IDatePicker,
  IWedding_Website_Section_GiftListField,
  IWedding_Website_Section_PhotosField,
  IWedding_Website_Section_QuestionAnswerField,
  Layout,
} from '@bridebook/models/source/models/Weddings.types';
import { TSectionName } from '@bridebook/web/lib/wedding-website/types';
import { WeddingActionTypes } from '../action-types';
import { IWeddingWebsiteDataState, WebsiteSectionUpdate } from '../types';

export const setWebsiteShouldPrefill = (payload: boolean) => ({
  type: WeddingActionTypes.WEBSITE_SET_SHOULD_PREFILL,
  payload,
});

export const setWebsiteDraft = (payload: {
  draft: IWeddingWebsiteDataState['draft'];
  dirty?: boolean;
}) => ({
  type: WeddingActionTypes.WEBSITE_SET_DRAFT,
  payload,
});

export const updateWebsiteDraft = (payload: {
  draft: Partial<IWeddingWebsiteDataState['draft']>;
  dirty?: boolean;
}) => ({
  type: WeddingActionTypes.WEBSITE_UPDATE_DRAFT,
  payload,
});

export const resetWebsiteDraft = () => ({
  type: WeddingActionTypes.WEBSITE_RESET_DRAFT,
});

export const setWebsiteTheme = (payload: {
  theme: { font: string; palette: string };
  dirty?: boolean;
}) => ({
  type: WeddingActionTypes.WEBSITE_SET_THEME,
  payload,
});

export const setWebsiteThemeFont = (payload: { font: string; dirty?: boolean }) => ({
  type: WeddingActionTypes.WEBSITE_SET_THEME_FONT,
  payload,
});

export const setWebsiteThemePalette = (payload: { palette: string; dirty?: boolean }) => ({
  type: WeddingActionTypes.WEBSITE_SET_THEME_PALETTE,
  payload,
});

export const setWebsiteLayoutVersion = (payload: { layout: Layout; dirty?: boolean }) => ({
  type: WeddingActionTypes.WEBSITE_SET_LAYOUT_VERSION,
  payload,
});

/**
 * A classic theme/font pick applied together with the layout in ONE action (leaving Motif via a classic
 * tile). A single state transition makes the draft-sync epic write one consistent config; two actions
 * would persist an intermediate state whose server echo flickers the UI.
 */
export const setWebsiteClassicDesign = (payload: {
  layout: Layout;
  theme?: { font: string; palette: string };
  font?: string;
  dirty?: boolean;
}) => ({
  type: WeddingActionTypes.WEBSITE_SET_CLASSIC_DESIGN,
  payload,
});

/**
 * Set the ACTIVE Motif selection in the draft website config: which theme the editor targets and its
 * current tail cssHash. The single source of truth for the active theme (survives publish via the published
 * config copy). Flows through `updateWeddingDraftWebsiteConfig` like the other config setters, so the write
 * persists and the dirty flag derives naturally — no `markWebsiteDirty` escape hatch. Pass `dirty: false`
 * for a pure selection (e.g. bootstrap) that is not itself an unpublished change.
 */
export const setMotifSelection = (payload: {
  motifThemeId: string;
  cssHash?: string;
  dirty?: boolean;
}) => ({
  type: WeddingActionTypes.WEBSITE_SET_MOTIF_SELECTION,
  payload,
});

export const setWebsitePin = (payload: { pin: string; dirty?: boolean }) => ({
  type: WeddingActionTypes.WEBSITE_SET_PIN,
  payload,
});

export const setWebsitePublished = (payload: { published: boolean; dirty?: boolean }) => ({
  type: WeddingActionTypes.WEBSITE_SET_PUBLISHED,
  payload,
});

export const setWebsitePartners = (payload: [string, string]) => ({
  type: WeddingActionTypes.WEBSITE_SET_PARTNERS,
  payload,
});

export const setWebsiteWeddingDate = (payload: IDatePicker) => ({
  type: WeddingActionTypes.WEBSITE_SET_WEDDING_DATE,
  payload,
});

export const updateWebsiteSection = (payload: WebsiteSectionUpdate) => ({
  type: WeddingActionTypes.WEBSITE_UPDATE_SECTION,
  payload,
});

export const reorderWebsiteSections = (sections: TSectionName[]) => ({
  type: WeddingActionTypes.WEBSITE_REORDER_SECTIONS,
  payload: sections,
});

export const setWebsiteSectionVisibility = (payload: { name: string; visible: boolean }) => ({
  type: WeddingActionTypes.WEBSITE_SET_SECTION_VISIBILITY,
  payload,
});

export const setRsvpVisible = (payload: { visible: boolean; dirty?: boolean }) => ({
  type: WeddingActionTypes.WEBSITE_SET_RSVP_VISIBLE,
  payload,
});

export const addWebsiteQuestionAnswer = (payload: {
  name: string;
  question: string;
  answer: string;
}) => ({
  type: WeddingActionTypes.WEBSITE_ADD_QUESTION_ANSWER,
  payload,
});

export const updateWebsiteQuestionAnswer = (payload: {
  name: string;
  index: number;
  question: string;
  answer: string;
}) => ({
  type: WeddingActionTypes.WEBSITE_UPDATE_QUESTION_ANSWER,
  payload,
});

export const removeWebsiteQuestionAnswer = (payload: { name: string; index: number }) => ({
  type: WeddingActionTypes.WEBSITE_REMOVE_QUESTION_ANSWER,
  payload,
});

export const setWebsiteQuestionAnswerSectionData = (payload: {
  name: string;
  data: IWedding_Website_Section_QuestionAnswerField['data'];
}) => ({
  type: WeddingActionTypes.WEBSITE_SET_QUESTION_ANSWER_SECTION_DATA,
  payload,
});

export const updateWebsiteTextField = (payload: { name: string; value: string }) => ({
  type: WeddingActionTypes.WEBSITE_UPDATE_TEXT_FIELD,
  payload,
});

export const updateWebsitePhotos = (payload: {
  name: string;
  data: IWedding_Website_Section_PhotosField['data'];
}) => ({
  type: WeddingActionTypes.WEBSITE_UPDATE_PHOTOS,
  payload,
});

export const addWebsitePhoto = (payload: {
  name: string;
  data: IWedding_Website_Section_PhotosField['data'][number];
}) => ({
  type: WeddingActionTypes.WEBSITE_ADD_PHOTO,
  payload,
});

export const addWebsitePhotoAtIndex = (payload: {
  name: string;
  data: IWedding_Website_Section_PhotosField['data'][number];
  index: number;
}) => ({
  type: WeddingActionTypes.WEBSITE_ADD_PHOTO_AT_INDEX,
  payload,
});

export const removeWebsitePhoto = (payload: { name: string; photo: string }) => ({
  type: WeddingActionTypes.WEBSITE_REMOVE_PHOTO,
  payload,
});

export const toggleWebsiteOnboardingModalOpen = (payload: boolean) => ({
  type: WeddingActionTypes.WEBSITE_ONBOARDING_MODAL_TOGGLE_OPEN,
  payload,
});

export const setActiveWebsiteOnboardingStep = (payload: number) => ({
  type: WeddingActionTypes.WEBSITE_ONBOARDING_SET_ACTIVE_STEP,
  payload,
});

export const stepWebsiteOnboardingBackward = () => ({
  type: WeddingActionTypes.WEBSITE_ONBOARDING_STEP_BACKWARD,
});

export const stepWebsiteOnboardingForward = (payload: {
  onboardingStepNumber: number;
  onboardingStepContent: string;
}) => ({
  type: WeddingActionTypes.WEBSITE_ONBOARDING_STEP_FORWARD,
  payload,
});

export const setWebsiteDesignThemeTabOpen = (payload: boolean) => ({
  type: WeddingActionTypes.WEBSITE_DESIGN_SET_THEME_TAB_OPEN,
  payload,
});

export const setWebsiteDesignCustomiseTabOpen = (payload: boolean) => ({
  type: WeddingActionTypes.WEBSITE_DESIGN_SET_CUSTOMISE_TAB_OPEN,
  payload,
});

export const resetWebsiteDesignTabState = () => ({
  type: WeddingActionTypes.WEBSITE_DESIGN_RESET_TAB_STATE,
});

export const updateWebsiteGiftList = (
  payload: Partial<IWedding_Website_Section_GiftListField['data']>,
) => ({
  type: WeddingActionTypes.WEBSITE_UPDATE_GIFT_LIST,
  payload,
});

export const updatePublishedPrimaryVenueSupplier = (payload: { supplier: string }) => ({
  type: WeddingActionTypes.WEBSITE_UPDATE_PUBLISHED_PRIMARY_VENUE_SUPPLIER,
  payload,
});

export const updateWebsiteHotels = (payload: { message?: string }) => ({
  type: WeddingActionTypes.WEBSITE_UPDATE_HOTELS,
  payload,
});

export const onFetchWebsiteDraftToggle = ({ show }: { show: boolean }) => ({
  type: WeddingActionTypes.WEBSITE_FETCH_DRAFT_MODAL_TOGGLE,
  payload: { show },
});

export const updateDraftTimestamp = ({ updatedAt }: { updatedAt: number }) => ({
  type: WeddingActionTypes.UPDATE_DRAFT_TIMESTAMP,
  payload: { updatedAt },
});

/**
 * The Motif preview's client-only echo of the latest palette/font taps (`{ palette?, fonts? }`).
 * Applied instantly as inline `--ww-*` vars on `.bb-ww` (no CSS refetch) so a swatch/font tap
 * restyles the preview in 0ms; the theme `css` stays the source of truth (the tap burns into it via
 * the REST action). Pass channels to apply, or null to clear (so the served CSS wins).
 */
export const setMotifPreviewOverride = (override: ThemeOverrideChannels | null) => ({
  type: WeddingActionTypes.MOTIF_SET_PREVIEW_OVERRIDE,
  payload: { override },
});

/**
 * Merge ONE channel into the Motif preview echo, keeping the other — a palette tap after a
 * font tap must not visually revert the fonts (taps never refetch CSS, so a whole-object replace would
 * drop the other channel's inline vars). Cards dispatch this; `setMotifPreviewOverride(null)` stays the
 * only clear.
 */
export const mergeMotifPreviewOverride = (patch: ThemeOverrideChannels) => ({
  type: WeddingActionTypes.MOTIF_MERGE_PREVIEW_OVERRIDE,
  payload: { patch },
});

/**
 * Drop ONE channel from the Motif preview echo, keeping the other. Used when a tap's burn fails: the
 * failed channel falls back to the served css while a still-pending choice in the other channel stays on
 * screen. Clears the whole echo once no channel is left.
 */
export const clearMotifPreviewOverrideChannel = (channel: keyof ThemeOverrideChannels) => ({
  type: WeddingActionTypes.MOTIF_CLEAR_PREVIEW_OVERRIDE_CHANNEL,
  payload: { channel },
});

/** A tap/echo burn POST has started — increments the in-flight count that gates Publish (see pending-burns.ts). */
export const motifBurnStarted = () => ({
  type: WeddingActionTypes.WEBSITE_MOTIF_BURN_STARTED,
});

/** A burn POST has settled — decrements the in-flight count (clamped at 0 in the reducer). */
export const motifBurnSettled = () => ({
  type: WeddingActionTypes.WEBSITE_MOTIF_BURN_SETTLED,
});

/**
 * Flag a just-prompted theme as pending so the gallery stands a tile in before its server entry lands.
 */
export const setMotifPendingGeneration = (payload: { motifThemeId: string; sentAt: number }) => ({
  type: WeddingActionTypes.MOTIF_SET_PENDING_GENERATION,
  payload,
});

/** Drop one theme's pending flag (turn settled, or theme deleted). */
export const clearMotifPendingGeneration = (payload: { motifThemeId: string }) => ({
  type: WeddingActionTypes.MOTIF_CLEAR_PENDING_GENERATION,
  payload,
});

/**
 * Mark the website as having unpublished changes (enables "Publish changes"). Motif theme edits (palette,
 * font, layout) write to the per-wedding theme doc, NOT to `draft.website`, so the normal dirty check can't
 * see them — this flips the flag explicitly. Cleared on publish (the draft resets dirty → false).
 */
export const markWebsiteDirty = () => ({
  type: WeddingActionTypes.WEBSITE_MARK_DIRTY,
});
